'use client';
import { useEffect, useRef, useState } from 'react';
import { BREAKPOINTS } from '@/app/constants';
import { cn } from '@/app/utils';
import { playMediaSafely } from '@/app/utils/client';

// 定义过渡类型
export interface TransitionProps {
  duration: number;
  ease: string;
  delay: number;
}

interface VideoLazyLoadProps {
  className?: string;
  wrapperStyle?: React.CSSProperties;
  poster: string;
  videoSrc: string;
  mobilePoster?: string;
  /**
   * 移动端视频资源。不传时复用 videoSrc;传 null 时移动端只展示 poster，不加载视频。
   */
  mobileVideoSrc?: string | null;
  videoClassName?: string;
  priority?: boolean;
  hoverPlay?: boolean; // 是否启用悬停播放功能
  /** 是否启用视频加载，false 时只显示 poster，不加载视频 */
  enabled?: boolean;
  /** 是否播放已加载的视频；用于提前预热下一条但不播放 */
  shouldPlay?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  /** 封面图 alt 文本 */
  alt?: string;
}

const VideoLazyLoad = ({
  poster,
  videoSrc,
  mobilePoster,
  mobileVideoSrc,
  className,
  wrapperStyle,
  videoClassName,
  priority = false,
  hoverPlay = false,
  enabled = true,
  shouldPlay = enabled,
  preload = priority ? 'metadata' : 'none',
  alt = 'AI generated video',
}: VideoLazyLoadProps) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVideoVisible(true);
          setHasBeenVisible(true);
        } else {
          setVideoVisible(false);
        }
      });
    }, options);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled || !shouldPlay) {
      videoRef.current?.pause();
      return;
    }

    if (!videoRef.current || !videoLoaded) return;

    // 悬停播放模式：仅在悬停时播放, hover play 模式不重置，保持播放进度
    if (hoverPlay) {
      if (isHovering && videoVisible) {
        playMediaSafely(videoRef.current);
      } else {
        videoRef.current.pause();
      }
      return;
    }
    // 默认模式：在视图中自动播放, 离开视口时重置为 0，下次进入时从头开始
    if (videoVisible) {
      playMediaSafely(videoRef.current);
      return;
    }
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, [enabled, shouldPlay, videoLoaded, videoVisible, isHovering, hoverPlay]);

  const hasMobileVideoSource = mobileVideoSrc !== undefined;
  const shouldRenderMobileVideo = Boolean(mobileVideoSrc);
  const desktopSourceMedia = hasMobileVideoSource ? `(min-width: ${BREAKPOINTS.MD_MIN}px)` : undefined;
  const mobileSourceMedia = `(max-width: ${BREAKPOINTS.SM_MAX}px)`;

  const handleVideoLoad = () => {
    setVideoLoaded(true);
    if (videoRef.current && shouldPlay && !hoverPlay) {
      videoRef.current.currentTime = 0;
      playMediaSafely(videoRef.current);
    }
  };

  const handleMouseEnter = () => {
    if (hoverPlay) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (hoverPlay) {
      setIsHovering(false);
    }
  };

  return (
    <div
      className={cn('relative aspect-video', className)}
      ref={containerRef}
      style={wrapperStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 总是显示海报图片 */}
      <picture>
        {mobilePoster ? <source media={mobileSourceMedia} srcSet={mobilePoster} /> : null}
        <img
          src={poster}
          alt={alt}
          decoding="async"
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          width={1920}
          height={1080}
          className={`size-full object-cover object-center ${videoLoaded && videoVisible && (!hoverPlay || isHovering) ? 'hidden' : 'block'}`}
        />
      </picture>

      {/* enabled=false 时不加载视频，仅显示 poster */}
      {enabled && hasBeenVisible && videoSrc && (
        <video
          ref={videoRef}
          autoPlay={shouldPlay && !hoverPlay}
          loop
          controls={false}
          muted
          preload={preload}
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          className={cn('h-full w-full object-cover object-center', videoClassName, {
            block: videoLoaded && (!hoverPlay || isHovering),
            hidden: !videoLoaded || (hoverPlay && !isHovering),
          })}
          onLoadedData={handleVideoLoad}
        >
          {shouldRenderMobileVideo ? (
            <source media={mobileSourceMedia} src={mobileVideoSrc ?? undefined} type="video/mp4" />
          ) : null}
          <source media={desktopSourceMedia} src={videoSrc} type="video/mp4" />
        </video>
      )}
    </div>
  );
};

export default VideoLazyLoad;
